import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { Suspense, useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { adminListRequests, adminUpdateRequest } from "@/lib/admin.functions";
import { REQUEST_STATUSES, type RequestStatus } from "@/lib/cms.types";
import { COUNTRY_BY_CODE } from "@/lib/countries";
import { AdminPagination } from "@/components/admin/AdminPagination";
import { adminPageCount, parseAdminPagination } from "@/lib/admin-pagination";

export const Route = createFileRoute("/admin/requests")({
  staticData: { sitemap: false },
  validateSearch: parseAdminPagination,
  head: () => ({ meta: [
    { title: "Requests admin | factory.online" },
    { name: "description", content: "Manage factory.online demo, partnership and contact requests." },
    { property: "og:title", content: "Requests admin | factory.online" },
    { property: "og:description", content: "Manage factory.online demo, partnership and contact requests." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: RequestsPageBoundary,
});

function RequestsPageBoundary() {
  return <Suspense fallback={<div className="mt-10 flex justify-center"><Loader2 className="size-6 animate-spin text-primary" aria-label="Loading requests" /></div>}><RequestsPage /></Suspense>;
}

function RequestsPage() {
  const { page, pageSize } = Route.useSearch();
  const navigate = Route.useNavigate();
  const list = useServerFn(adminListRequests);
  const update = useServerFn(adminUpdateRequest);
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery({
    queryKey: ["admin-requests", page, pageSize],
    queryFn: () => list({ data: { page, pageSize } }),
    staleTime: 30_000,
  });
  const pages = adminPageCount(data.total, pageSize);
  useEffect(() => {
    if (page > pages) void navigate({ search: (prev) => ({ ...prev, page: pages }), replace: true });
  }, [page, pages, navigate]);

  const statusMutation = useMutation({
    mutationFn: (input: { id: string; status: RequestStatus }) => update({ data: input }),
    onSuccess: () => {
      toast.success("Status updated");
      void queryClient.invalidateQueries({ queryKey: ["admin-requests"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <>
      <h1 className="font-display text-3xl font-bold">Requests</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Demo, partnership and contact submissions from every language version. Historical trial requests remain visible.
      </p>

      {!data.rows.length ? (
        <p className="mt-10 text-sm text-muted-foreground">No requests yet.</p>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-xs tracking-wide text-muted-foreground uppercase">
              <tr>
                <th className="p-4">Date</th>
                <th className="p-4">Type</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Company</th>
                <th className="p-4">Country</th>
                <th className="p-4">Industry</th>
                <th className="p-4">Message</th>
                <th className="p-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.id} className="border-t border-border align-top">
                  <td className="p-4 whitespace-nowrap text-muted-foreground">
                    {new Date(row.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <Badge variant="secondary">{row.kind}</Badge>
                    <span className="ms-2 text-xs text-muted-foreground uppercase">
                      {row.locale}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="font-medium">{row.full_name}</div>
                    <a href={`mailto:${row.email}`} className="text-muted-foreground hover:text-primary">
                      {row.email}
                    </a>
                    {row.phone ? (
                      <div className="text-muted-foreground">{row.phone}</div>
                    ) : null}
                  </td>
                  <td className="p-4 text-muted-foreground">{row.company ?? "—"}</td>
                  <td className="p-4 text-muted-foreground">
                    {row.country ? (COUNTRY_BY_CODE[row.country] ?? row.country) : "—"}
                  </td>
                  <td className="p-4 text-muted-foreground">{row.industry ?? "—"}</td>
                  <td className="max-w-sm p-4 text-muted-foreground">{row.message ?? "—"}</td>
                  <td className="p-4">
                    <Select
                      value={row.status}
                      onValueChange={(value) =>
                        statusMutation.mutate({ id: row.id, status: value as RequestStatus })
                      }
                    >
                      <SelectTrigger className="w-36" aria-label="Request status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REQUEST_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AdminPagination page={page} pageSize={pageSize} total={data.total}
        onPageChange={(next) => void navigate({ search: (prev) => ({ ...prev, page: next }) })}
        onPageSizeChange={(size) => void navigate({ search: (prev) => ({ ...prev, page: 1, pageSize: size }) })}
      />
    </>
  );
}
